import { useCallback, useEffect, useMemo, useState } from 'react';
import { StandardDataGrid, Toast, authHeaders, gridActionButton, s } from './listPageStyles';

/* ─── Sabitler ─────────────────────────────────────────────── */
const SOURCE_TYPES = [
  { value: 1, label: 'API' },
  { value: 2, label: 'Veritabanı' },
  { value: 3, label: 'Excel' },
  { value: 4, label: 'JSON' },
];

const QUERY_TYPES = [
  { value: 1, label: 'Ham SQL' },
  { value: 2, label: 'Saklı Yordam' },
  { value: 3, label: 'View' },
];

const JSON_MODES = [
  { value: 1, label: 'Dosya' },
  { value: 2, label: 'Ham JSON' },
];

const TRANSFORM_TYPES = [
  { value: 1, label: 'Temizle' },
  { value: 2, label: 'Formatla' },
  { value: 3, label: 'Esle' },
  { value: 4, label: 'Filtrele' },
];

const FILTER_OPERATORS = [
  { value: 'eq', label: 'Esittir' },
  { value: 'ne', label: 'Esit degildir' },
  { value: 'gt', label: 'Buyuktur' },
  { value: 'gte', label: 'Buyuk veya esittir' },
  { value: 'lt', label: 'Kucuktur' },
  { value: 'lte', label: 'Kucuk veya esittir' },
  { value: 'contains', label: 'Icerir' },
  { value: 'notContains', label: 'Icermez' },
  { value: 'isNull', label: 'Bos/null' },
  { value: 'notNull', label: 'Bos/null degil' },
  { value: 'notEmpty', label: 'Bos metin degil' },
];

const CASING_OPTIONS = [
  { value: '', label: 'Degistirme' },
  { value: 'upper', label: 'Buyuk harf' },
  { value: 'lower', label: 'Kucuk harf' },
  { value: 'title', label: 'Bas harfleri buyuk' },
];

const STATUS_MAP = {
  0: { label: 'Hiç Çalışmadı', color: '#64748b' },
  1: { label: 'Çalışıyor',      color: 'var(--accent)' },
  2: { label: 'Başarılı',       color: 'var(--success)' },
  3: { label: 'Başarısız',      color: '#E53E3E' },
  4: { label: 'Kısmen Başarılı',color: 'var(--warn)' },
};

const EMPTY_API    = { baseUrl: '', authHeaderName: '', authHeaderValue: '', paginationPath: '', nextPageUrlPath: '', timeoutSeconds: 30 };
const EMPTY_DB     = { queryType: 1, query: '', parameters: '{}', connectionStringName: '' };
const EMPTY_EXCEL  = { filePath: '', sheetName: '', headerRowIndex: 1, dataStartRowIndex: 2 };
const EMPTY_JSON   = { mode: 1, source: '', rootPath: '' };

const EMPTY_FORM = {
  name: '', description: '', sourceType: 1, cronExpression: '',
  transformStepsJson: '[]',
  api: { ...EMPTY_API },
  db: { ...EMPTY_DB },
  excel: { ...EMPTY_EXCEL },
  json: { ...EMPTY_JSON },
};

const DEFAULT_SCHEDULE = {
  mode: 'daily',
  everyMinutes: 15,
  minute: 0,
  hour: 8,
  dayOfWeek: 1,
  dayOfMonth: 1,
};

/* ─── Yardımcılar ───────────────────────────────────────────── */
function compareValues(left, right, dir) {
  const a = left ?? '';
  const b = right ?? '';
  return dir === 'asc'
    ? String(a).localeCompare(String(b), 'tr', { sensitivity: 'base' })
    : String(b).localeCompare(String(a), 'tr', { sensitivity: 'base' });
}

function buildSourceConfig(form) {
  const sourceType = Number(form.sourceType);

  switch (sourceType) {
    case 1: return {
      sourceType: 1,
      baseUrl: form.api.baseUrl,
      authHeaderName: form.api.authHeaderName || null,
      authHeaderValue: form.api.authHeaderValue || null,
      paginationPath: form.api.paginationPath || null,
      nextPageUrlPath: form.api.nextPageUrlPath || null,
      timeoutSeconds: Number(form.api.timeoutSeconds) || 30,
    };
    case 2: {
      let params = {};
      try { params = JSON.parse(form.db.parameters || '{}'); } catch { params = {}; }
      return {
        sourceType: 2,
        queryType: Number(form.db.queryType),
        query: form.db.query,
        parameters: params,
        connectionStringName: form.db.connectionStringName || null,
      };
    }
    case 3: return {
      sourceType: 3,
      filePath: form.excel.filePath,
      sheetName: form.excel.sheetName || null,
      headerRowIndex: Number(form.excel.headerRowIndex) || 1,
      dataStartRowIndex: Number(form.excel.dataStartRowIndex) || 2,
    };
    case 4: return {
      sourceType: 4,
      mode: Number(form.json.mode),
      source: form.json.source,
      rootPath: form.json.rootPath || null,
    };
    default: return {};
  }
}

async function readApiError(response, fallback) {
  const text = await response.text().catch(() => '');
  if (!text) return fallback;

  try {
    const json = JSON.parse(text);
    if (json.message) return json.message;
    if (json.title) return json.title;
    if (json.errors && typeof json.errors === 'object') {
      return Object.entries(json.errors)
        .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
        .join(' | ');
    }
    return JSON.stringify(json);
  } catch {
    return text;
  }
}

function clampNumber(value, min, max, fallback) {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.min(Math.max(parsed, min), max);
}

function buildCronExpression(schedule) {
  const minute = clampNumber(schedule.minute, 0, 59, 0);
  const hour = clampNumber(schedule.hour, 0, 23, 8);
  const everyMinutes = clampNumber(schedule.everyMinutes, 1, 59, 15);
  const dayOfWeek = clampNumber(schedule.dayOfWeek, 0, 6, 1);
  const dayOfMonth = clampNumber(schedule.dayOfMonth, 1, 31, 1);

  switch (schedule.mode) {
    case 'everyMinutes': return `*/${everyMinutes} * * * *`;
    case 'hourly': return `${minute} * * * *`;
    case 'weekly': return `${minute} ${hour} * * ${dayOfWeek}`;
    case 'monthly': return `${minute} ${hour} ${dayOfMonth} * *`;
    case 'daily':
    default: return `${minute} ${hour} * * *`;
  }
}

function describeSchedule(schedule) {
  const minute = String(clampNumber(schedule.minute, 0, 59, 0)).padStart(2, '0');
  const hour = String(clampNumber(schedule.hour, 0, 23, 8)).padStart(2, '0');
  const dayNames = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

  switch (schedule.mode) {
    case 'everyMinutes': return `Her ${clampNumber(schedule.everyMinutes, 1, 59, 15)} dakikada bir`;
    case 'hourly': return `Her saat ${minute}. dakikada`;
    case 'weekly': return `Her ${dayNames[clampNumber(schedule.dayOfWeek, 0, 6, 1)]} ${hour}:${minute}`;
    case 'monthly': return `Her ayın ${clampNumber(schedule.dayOfMonth, 1, 31, 1)}. günü ${hour}:${minute}`;
    case 'daily':
    default: return `Her gün ${hour}:${minute}`;
  }
}

function parseCronExpression(cron) {
  const text = (cron || '').trim();
  if (!text) return { ...DEFAULT_SCHEDULE };

  const parts = text.split(/\s+/);
  if (parts.length !== 5) return { ...DEFAULT_SCHEDULE };

  const [minute, hour, dayOfMonth, , dayOfWeek] = parts;

  if (minute.startsWith('*/') && hour === '*' && dayOfMonth === '*' && dayOfWeek === '*') {
    return { ...DEFAULT_SCHEDULE, mode: 'everyMinutes', everyMinutes: clampNumber(minute.slice(2), 1, 59, 15) };
  }

  if (/^\d+$/.test(minute) && hour === '*' && dayOfMonth === '*' && dayOfWeek === '*') {
    return { ...DEFAULT_SCHEDULE, mode: 'hourly', minute: clampNumber(minute, 0, 59, 0) };
  }

  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && dayOfMonth === '*' && /^\d+$/.test(dayOfWeek)) {
    return {
      ...DEFAULT_SCHEDULE,
      mode: 'weekly',
      minute: clampNumber(minute, 0, 59, 0),
      hour: clampNumber(hour, 0, 23, 8),
      dayOfWeek: clampNumber(dayOfWeek, 0, 6, 1),
    };
  }

  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && /^\d+$/.test(dayOfMonth) && dayOfWeek === '*') {
    return {
      ...DEFAULT_SCHEDULE,
      mode: 'monthly',
      minute: clampNumber(minute, 0, 59, 0),
      hour: clampNumber(hour, 0, 23, 8),
      dayOfMonth: clampNumber(dayOfMonth, 1, 31, 1),
    };
  }

  if (/^\d+$/.test(minute) && /^\d+$/.test(hour) && dayOfMonth === '*' && dayOfWeek === '*') {
    return {
      ...DEFAULT_SCHEDULE,
      mode: 'daily',
      minute: clampNumber(minute, 0, 59, 0),
      hour: clampNumber(hour, 0, 23, 8),
    };
  }

  return { ...DEFAULT_SCHEDULE };
}

function parseTransformStepsJson(json) {
  try {
    const parsed = JSON.parse(json || '[]');
    if (!Array.isArray(parsed)) return { steps: [], error: 'Donusum adimlari dizi formatinda olmali.' };
    return { steps: normalizeTransformOrders(parsed), error: null };
  } catch (err) {
    return { steps: [], error: err.message };
  }
}

function normalizeTransformOrders(steps) {
  return steps.map((step, index) => {
    const stepType = Number(step?.stepType || 1);
    const options = step?.options && typeof step.options === 'object' && !Array.isArray(step.options)
      ? step.options
      : {};

    return {
      stepType,
      order: index + 1,
      options: normalizeTransformOptions(stepType, options),
    };
  });
}

function normalizeTransformOptions(stepType, options) {
  switch (Number(stepType)) {
    case 1: return {
      trimStrings: options.trimStrings !== false,
      nullifyEmpty: options.nullifyEmpty !== false,
      removeNullRows: options.removeNullRows === true,
      keepOnlyColumns: options.keepOnlyColumns === true,
      columns: arrayOfText(options.columns),
    };
    case 2: return {
      dateColumns: arrayOfText(options.dateColumns),
      dateFormat: scalarToText(options.dateFormat) || 'yyyy-MM-dd',
      numberColumns: arrayOfText(options.numberColumns),
      decimalPlaces: Number(scalarToText(options.decimalPlaces)) || 2,
      casingColumns: arrayOfText(options.casingColumns),
      casing: scalarToText(options.casing),
    };
    case 3: return {
      columns: normalizeTextMap(options.columns),
      values: normalizeNestedTextMap(options.values),
    };
    case 4: return {
      column: scalarToText(options.column),
      operator: scalarToText(options.operator) || 'eq',
      value: scalarToText(options.value),
    };
    default: return options;
  }
}

function stringifyTransformSteps(steps) {
  return JSON.stringify(normalizeTransformOrders(steps), null, 2);
}

function defaultOptionsForStep(stepType) {
  switch (Number(stepType)) {
    case 1: return { trimStrings: true, nullifyEmpty: true, removeNullRows: false };
    case 2: return { dateColumns: [], dateFormat: 'yyyy-MM-dd', numberColumns: [], decimalPlaces: 2, casingColumns: [], casing: '' };
    case 3: return { columns: {}, values: {} };
    case 4: return { column: '', operator: 'eq', value: '' };
    default: return {};
  }
}

function splitList(value) {
  return String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
}

function listToText(value) {
  if (Array.isArray(value)) return value.map(scalarToText).filter(Boolean).join(', ');
  return scalarToText(value);
}

function scalarToText(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function arrayOfText(value) {
  if (Array.isArray(value)) return value.map(scalarToText).filter(Boolean);
  const text = scalarToText(value);
  return text ? splitList(text) : [];
}

function normalizeTextMap(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.entries(value).reduce((acc, [key, val]) => {
    const text = scalarToText(val);
    if (key && text) acc[key] = text;
    return acc;
  }, {});
}

function normalizeNestedTextMap(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.entries(value).reduce((acc, [key, map]) => {
    const normalized = normalizeTextMap(map);
    if (key && Object.keys(normalized).length > 0) acc[key] = normalized;
    return acc;
  }, {});
}

function parseRenameMap(value) {
  return String(value || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)
    .reduce((acc, line) => {
      const separatorIndex = line.indexOf('=');
      if (separatorIndex > 0) {
        const key = line.slice(0, separatorIndex).trim();
        const val = line.slice(separatorIndex + 1).trim();
        if (key && val) acc[key] = val;
      }
      return acc;
    }, {});
}

function renameMapToText(value) {
  if (!value || typeof value !== 'object') return '';
  return Object.entries(value)
    .map(([key, val]) => `${key}=${scalarToText(val)}`)
    .filter(line => !line.endsWith('='))
    .join('\n');
}

function parseValueMap(value) {
  return String(value || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)
    .reduce((acc, line) => {
      const separatorIndex = line.indexOf(':');
      if (separatorIndex <= 0) return acc;

      const column = line.slice(0, separatorIndex).trim();
      const pairs = line.slice(separatorIndex + 1).split(',');
      const map = {};

      pairs.forEach(pair => {
        const pairSeparator = pair.indexOf('=');
        if (pairSeparator <= 0) return;
        const from = pair.slice(0, pairSeparator).trim();
        const to = pair.slice(pairSeparator + 1).trim();
        if (from && to) map[from] = to;
      });

      if (column && Object.keys(map).length > 0) acc[column] = map;
      return acc;
    }, {});
}

function valueMapToText(value) {
  if (!value || typeof value !== 'object') return '';
  return Object.entries(value)
    .map(([column, map]) => {
      if (!map || typeof map !== 'object') return '';
      const pairs = Object.entries(map)
        .map(([from, to]) => `${from}=${scalarToText(to)}`)
        .filter(pair => !pair.endsWith('='))
        .join(', ');
      return pairs ? `${column}: ${pairs}` : '';
    })
    .filter(Boolean)
    .join('\n');
}

function mapPipelineToForm(pipeline) {
  const sourceConfig = pipeline?.sourceConfig || {};
  const transformSteps = Array.isArray(pipeline?.transformSteps) ? pipeline.transformSteps : [];

  return {
    name: pipeline?.name || '',
    description: pipeline?.description || '',
    sourceType: Number(pipeline?.sourceType || 1),
    cronExpression: pipeline?.cronExpression || '',
    transformStepsJson: JSON.stringify(transformSteps, null, 2),
    api: {
      ...EMPTY_API,
      baseUrl: sourceConfig.baseUrl || '',
      authHeaderName: sourceConfig.authHeaderName || '',
      authHeaderValue: sourceConfig.authHeaderValue || '',
      paginationPath: sourceConfig.paginationPath || '',
      nextPageUrlPath: sourceConfig.nextPageUrlPath || '',
      timeoutSeconds: sourceConfig.timeoutSeconds ?? 30,
    },
    db: {
      ...EMPTY_DB,
      queryType: sourceConfig.queryType ?? 1,
      query: sourceConfig.query || '',
      parameters: JSON.stringify(sourceConfig.parameters || {}, null, 2),
      connectionStringName: sourceConfig.connectionStringName || '',
    },
    excel: {
      ...EMPTY_EXCEL,
      filePath: sourceConfig.filePath || '',
      sheetName: sourceConfig.sheetName || '',
      headerRowIndex: sourceConfig.headerRowIndex ?? 1,
      dataStartRowIndex: sourceConfig.dataStartRowIndex ?? 2,
    },
    json: {
      ...EMPTY_JSON,
      mode: sourceConfig.mode ?? 1,
      source: sourceConfig.source || '',
      rootPath: sourceConfig.rootPath || '',
    },
  };
}

/* ─── Bileşen ───────────────────────────────────────────────── */
export function EtlPipelinePageLegacy() {
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [selected, setSelected]       = useState(null);
  const [modal, setModal]             = useState(null);   // 'new' | 'view' | 'logs'
  const [form, setForm]               = useState(EMPTY_FORM);
  const [toast, setToast]             = useState(null);
  const [logs, setLogs]               = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState(DEFAULT_SCHEDULE);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [sortField, setSortField]       = useState('name');
  const [sortDir, setSortDir]           = useState('asc');
  const [filterInput, setFilterInput]   = useState('');
  const [filterText, setFilterText]     = useState('');
  const [pageSize, setPageSize]         = useState(10);
  const [currentPage, setCurrentPage]   = useState(1);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  /* Listeyi çek */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/Etl/pipelines', { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRows(Array.isArray(json.data) ? json.data : (Array.isArray(json) ? json : []));
    } catch (err) {
      showToast('error', `Pipeline listesi yüklenemedi: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* Filtre / sıralama / sayfalama */
  const filteredRows = useMemo(() => {
    const q = filterText.trim().toLocaleLowerCase('tr-TR');
    if (!q) return rows;
    return rows.filter(r =>
      (r.name || '').toLocaleLowerCase('tr-TR').includes(q) ||
      (r.description || '').toLocaleLowerCase('tr-TR').includes(q)
    );
  }, [rows, filterText]);

  const sortFieldKey = sortField ? sortField.charAt(0).toLowerCase() + sortField.slice(1) : '';
  const sortedRows = useMemo(() =>
    [...filteredRows].sort((a, b) => compareValues(a[sortFieldKey], b[sortFieldKey], sortDir)),
    [filteredRows, sortFieldKey, sortDir]
  );

  const totalCount  = sortedRows.length;
  const visibleRows = sortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const transformConfig = useMemo(
    () => parseTransformStepsJson(form.transformStepsJson),
    [form.transformStepsJson]
  );

  /* Modal açma */
  const openNew = () => { setSelected(null); setForm(EMPTY_FORM); setModal('new'); };

  const openEdit = async (row) => {
    setSelected(row);
    setSaving(true);
    try {
      const res = await fetch(`/api/Etl/pipelines/${row.id}`, { headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || `HTTP ${res.status}`);
      const data = json.data || json;
      setForm(mapPipelineToForm(data));
      setModal('edit');
    } catch (err) {
      showToast('error', `Pipeline detayi yuklenemedi: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const openView = (row) => {
    setSelected(row);
    setModal('view');
  };

  const openLogs = async (row) => {
    setSelected(row);
    setModal('logs');
    setLogsLoading(true);
    try {
      const res = await fetch(`/api/Etl/pipelines/${row.id}/logs?top=50`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setLogs(Array.isArray(json.data) ? json.data : []);
    } catch (err) {
      showToast('error', `Loglar yüklenemedi: ${err.message}`);
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const closeModal = () => setModal(null);
  const closeScheduleModal = () => setScheduleModalOpen(false);

  const openScheduleModal = () => {
    setScheduleDraft(parseCronExpression(form.cronExpression));
    setScheduleModalOpen(true);
  };

  const applySchedule = () => {
    setF('cronExpression', buildCronExpression(scheduleDraft));
    setScheduleModalOpen(false);
  };

  /* Kaydet */
  const handleSave = async () => {
    if (!form.name.trim()) { showToast('error', 'Pipeline adı zorunludur'); return; }
    if (form.sourceType === 1 && !form.api.baseUrl.trim()) { showToast('error', 'API URL zorunludur'); return; }
    if (form.sourceType === 2 && !form.db.query.trim()) { showToast('error', 'Sorgu / SP adı zorunludur'); return; }
    if ((form.sourceType === 3) && !form.excel.filePath.trim()) { showToast('error', 'Dosya yolu zorunludur'); return; }
    if (form.sourceType === 4 && !form.json.source.trim()) { showToast('error', 'Kaynak zorunludur'); return; }

    let transformSteps;
    try { transformSteps = JSON.parse(form.transformStepsJson || '[]'); }
    catch { showToast('error', 'Dönüşüm adımları geçerli JSON değil'); return; }

    const body = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      sourceType: Number(form.sourceType),
      sourceConfig: buildSourceConfig(form),
      transformSteps,
      cronExpression: form.cronExpression.trim() || null,
    };

    setSaving(true);
    try {
      const isEdit = modal === 'edit' && selected?.id;
      const res = await fetch(isEdit ? `/api/Etl/pipelines/${selected.id}` : '/api/Etl/pipelines', {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(await readApiError(res, isEdit ? 'Kayıt güncellenemedi' : 'Kayıt eklenemedi'));
      }
      showToast('success', isEdit ? 'ETL Pipeline güncellendi' : 'ETL Pipeline oluşturuldu');
      closeModal();
      fetchData();
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row = selected) => {
    if (!row?.id) return;

    setDeleting(true);
    try {
      const res = await fetch(`/api/Etl/pipelines/${row.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Silme başarısız');
      setDeleteConfirmOpen(false);
      if (selected?.id === row.id) setSelected(null);
      showToast('success', 'ETL Pipeline silindi');
      fetchData();
    } catch (err) {
      showToast('error', err.message);
    } finally {
      setDeleting(false);
    }
  };

  /* Aktif et */
  const handleActivate = async (row) => {
    try {
      const res = await fetch(`/api/Etl/pipelines/${row.id}/activate`, {
        method: 'POST', headers: authHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Aktifleştirme başarısız');
      showToast('success', `Pipeline aktifleştirildi (job: ${json.data || '—'})`);
      fetchData();
    } catch (err) {
      showToast('error', err.message);
    }
  };

  /* Hemen çalıştır */
  const handleRun = async (row) => {
    try {
      const res = await fetch(`/api/Etl/pipelines/${row.id}/run`, {
        method: 'POST', headers: authHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Çalıştırma başarısız');
      showToast('success', `Pipeline kuyruğa alındı (id: ${json.data || '—'})`);
      fetchData();
    } catch (err) {
      showToast('error', err.message);
    }
  };

  /* Form yardımcıları */
  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setApi   = (key, val) => setForm(f => ({ ...f, api:   { ...f.api,   [key]: val } }));
  const setDb    = (key, val) => setForm(f => ({ ...f, db:    { ...f.db,    [key]: val } }));
  const setExcel = (key, val) => setForm(f => ({ ...f, excel: { ...f.excel, [key]: val } }));
  const setJson  = (key, val) => setForm(f => ({ ...f, json:  { ...f.json,  [key]: val } }));

  const setTransformSteps = (steps) =>
    setF('transformStepsJson', stringifyTransformSteps(steps));

  const addTransformStep = () => {
    const steps = transformConfig.error ? [] : transformConfig.steps;
    setTransformSteps([
      ...steps,
      { stepType: 1, order: steps.length + 1, options: defaultOptionsForStep(1) },
    ]);
  };

  const removeTransformStep = (index) => {
    setTransformSteps(transformConfig.steps.filter((_, i) => i !== index));
  };

  const moveTransformStep = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= transformConfig.steps.length) return;
    const steps = [...transformConfig.steps];
    [steps[index], steps[nextIndex]] = [steps[nextIndex], steps[index]];
    setTransformSteps(steps);
  };

  const updateTransformStep = (index, patch) => {
    setTransformSteps(transformConfig.steps.map((step, i) =>
      i === index ? { ...step, ...patch } : step
    ));
  };

  const updateTransformOption = (index, key, value) => {
    setTransformSteps(transformConfig.steps.map((step, i) =>
      i === index
        ? { ...step, options: { ...(step.options || {}), [key]: value } }
        : step
    ));
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString('tr-TR') : '—';

  const columns = [
    { field: 'name', headerName: 'Pipeline Adı', flex: 1, minWidth: 180, renderCell: (params) => <strong>{params.row.name}</strong> },
    {
      field: 'sourceType',
      headerName: 'Kaynak Tipi',
      width: 140,
      renderCell: (params) => {
        const st = SOURCE_TYPES.find(t => t.value === params.row.sourceType);
        return <span style={pill('#0073ea')}>{st?.label ?? params.row.sourceType}</span>;
      },
    },
    {
      field: 'cronExpression',
      headerName: 'Zamanlama',
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) => <code style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{params.row.cronExpression || '—'}</code>,
    },
    {
      field: 'lastStatus',
      headerName: 'Son Durum',
      width: 150,
      renderCell: (params) => {
        const status = STATUS_MAP[params.row.lastStatus] ?? STATUS_MAP[0];
        return <span style={{ color: status.color, fontWeight: 600, fontSize: 13 }}>● {status.label}</span>;
      },
    },
    { field: 'lastRunAt', headerName: 'Son Çalışma', width: 170, renderCell: (params) => <span style={{ color: 'var(--text-secondary)' }}>{fmtDate(params.row.lastRunAt)}</span> },
    {
      field: 'isActive',
      headerName: 'Aktif',
      width: 120,
      renderCell: (params) => (
        <span style={params.row.isActive ? s.statusActive : s.statusPassive}>
          {params.row.isActive ? '● Aktif' : '○ Pasif'}
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: 'İşlemler',
      width: 420,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: '100%', flexWrap: 'wrap' }}>
          <button style={{ ...gridActionButton, background: 'var(--warn)' }} onClick={(event) => { event.stopPropagation(); openEdit(params.row); }}>Düzenle</button>
          <button style={{ ...gridActionButton, background: '#E53E3E', minWidth: 48 }} onClick={(event) => { event.stopPropagation(); setSelected(params.row); setDeleteConfirmOpen(true); }}>Sil</button>
          {!params.row.isActive && params.row.cronExpression && (
            <button style={{ ...gridActionButton, background: 'var(--accent)' }} onClick={(event) => { event.stopPropagation(); handleActivate(params.row); }}>Aktifleştir</button>
          )}
          <button style={{ ...gridActionButton, background: 'var(--success)' }} onClick={(event) => { event.stopPropagation(); handleRun(params.row); }}>Çalıştır</button>
          <button style={{ ...gridActionButton, background: 'var(--accent)' }} onClick={(event) => { event.stopPropagation(); openLogs(params.row); }}>Loglar</button>
        </div>
      ),
    },
  ];

  /* ── Render ── */
  return (
    <div style={s.container}>

      {/* Başlık */}
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h2 style={s.pageTitle}>ETL Pipeline Tanımları</h2>
          <p style={s.pageSubtitle}>Veri aktarım pipeline'larını tanımlayın, aktifleştirin ve çalıştırın.</p>
        </div>
        <button style={s.btnPrimary} onClick={openNew}>+ Yeni Pipeline</button>
      </div>

      {/* Toolbar */}
      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <input
            style={s.filterInput}
            type="text"
            placeholder="Pipeline adı ile filtrele..."
            value={filterInput}
            onChange={e => setFilterInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setFilterText(filterInput); setCurrentPage(1); } }}
          />
          {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); setFilterText(''); setCurrentPage(1); }}>Temizle</button>}
          <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
        </div>
      </div>

      <StandardDataGrid
        rows={visibleRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={p => setCurrentPage(p)}
        onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, direction) => {
          setCurrentPage(1);
          setSortField(field || 'name');
          setSortDir(direction || 'asc');
        }}
        selectedId={selected?.id}
        onRowClick={row => setSelected(row)}
        onRowDoubleClick={row => openView(row)}
        exportFileName="etl-pipeline"
      />

      {/* ── Pipeline Modalı ── */}
      {(modal === 'new' || modal === 'edit') && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={{ ...s.modal, maxWidth: 680, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>{modal === 'edit' ? 'ETL Pipeline Düzenle' : 'Yeni ETL Pipeline'}</h3>
              <button style={s.closeBtn} onClick={closeModal}>×</button>
            </div>
            <div style={s.modalBody}>

              {/* Temel bilgiler */}
              <SectionLabel>Temel Bilgiler</SectionLabel>
              <div style={s.formGroup}>
                <label style={s.label}>Pipeline Adı *</label>
                <input style={s.input} value={form.name} autoFocus
                  onChange={e => setF('name', e.target.value)} placeholder="Örn: Müşteri Veri Aktarımı" />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Açıklama</label>
                <input style={s.input} value={form.description}
                  onChange={e => setF('description', e.target.value)} placeholder="İsteğe bağlı açıklama" />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ ...s.formGroup, flex: 1 }}>
                  <label style={s.label}>Kaynak Tipi *</label>
                  <select style={s.select} value={form.sourceType}
                    onChange={e => setF('sourceType', Number(e.target.value))}>
                    {SOURCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div style={{ ...s.formGroup, flex: 1 }}>
                  <label style={s.label}>Cron Zamanlama</label>
                  <div style={styles.cronFieldWrap}>
                    <input style={{ ...s.input, marginBottom: 0 }} value={form.cronExpression}
                      onChange={e => setF('cronExpression', e.target.value)} placeholder="Orn: 0 8 * * *" />
                    <button type="button" style={styles.cronBuilderButton} onClick={openScheduleModal}>
                      Olustur
                    </button>
                  </div>
                  <div style={styles.cronHint}>Dakika Saat Gun Ay HaftaGunu biciminde cron degeri.</div>
                </div>
              </div>

              {/* Kaynak Konfigürasyonu */}
              <SectionLabel>Kaynak Konfigürasyonu</SectionLabel>

              {form.sourceType === 1 && (
                <>
                  <div style={s.formGroup}>
                    <label style={s.label}>URL *</label>
                    <input style={s.input} value={form.api.baseUrl}
                      onChange={e => setApi('baseUrl', e.target.value)} placeholder="https://api.example.com/data" />
                  </div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ ...s.formGroup, flex: 1 }}>
                      <label style={s.label}>Auth Header Adı</label>
                      <input style={s.input} value={form.api.authHeaderName}
                        onChange={e => setApi('authHeaderName', e.target.value)} placeholder="Authorization" />
                    </div>
                    <div style={{ ...s.formGroup, flex: 1 }}>
                      <label style={s.label}>Auth Header Değeri</label>
                      <input style={s.input} value={form.api.authHeaderValue}
                        onChange={e => setApi('authHeaderValue', e.target.value)} placeholder="Bearer token..." />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ ...s.formGroup, flex: 1 }}>
                      <label style={s.label}>Veri Dizisi JSONPath</label>
                      <input style={s.input} value={form.api.paginationPath}
                        onChange={e => setApi('paginationPath', e.target.value)} placeholder="$.data" />
                    </div>
                    <div style={{ ...s.formGroup, flex: 1 }}>
                      <label style={s.label}>Sonraki Sayfa JSONPath</label>
                      <input style={s.input} value={form.api.nextPageUrlPath}
                        onChange={e => setApi('nextPageUrlPath', e.target.value)} placeholder="$.next_page" />
                    </div>
                    <div style={{ ...s.formGroup, width: 90 }}>
                      <label style={s.label}>Timeout (s)</label>
                      <input style={s.input} type="number" min="1" value={form.api.timeoutSeconds}
                        onChange={e => setApi('timeoutSeconds', e.target.value)} />
                    </div>
                  </div>
                </>
              )}

              {form.sourceType === 2 && (
                <>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ ...s.formGroup, width: 160 }}>
                      <label style={s.label}>Sorgu Tipi</label>
                      <select style={s.select} value={form.db.queryType}
                        onChange={e => setDb('queryType', Number(e.target.value))}>
                        {QUERY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div style={{ ...s.formGroup, flex: 1 }}>
                      <label style={s.label}>Bağlantı Adı</label>
                      <input style={s.input} value={form.db.connectionStringName}
                        onChange={e => setDb('connectionStringName', e.target.value)}
                        placeholder="Boş = uygulama DB" />
                    </div>
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>Sorgu / SP Adı *</label>
                    <textarea style={{ ...s.input, height: 80, resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }}
                      value={form.db.query}
                      onChange={e => setDb('query', e.target.value)}
                      placeholder="SELECT * FROM [User] WHERE IsActive = 1" />
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>Parametreler (JSON)</label>
                    <input style={{ ...s.input, fontFamily: 'monospace', fontSize: 13 }}
                      value={form.db.parameters}
                      onChange={e => setDb('parameters', e.target.value)}
                      placeholder='{"TopN": 100}' />
                  </div>
                </>
              )}

              {form.sourceType === 3 && (
                <>
                  <div style={s.formGroup}>
                    <label style={s.label}>Dosya Yolu *</label>
                    <input style={s.input} value={form.excel.filePath}
                      onChange={e => setExcel('filePath', e.target.value)} placeholder="C:\Imports\data.xlsx" />
                  </div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ ...s.formGroup, flex: 1 }}>
                      <label style={s.label}>Sayfa Adı</label>
                      <input style={s.input} value={form.excel.sheetName}
                        onChange={e => setExcel('sheetName', e.target.value)} placeholder="Boş = ilk sayfa" />
                    </div>
                    <div style={{ ...s.formGroup, width: 100 }}>
                      <label style={s.label}>Başlık Satırı</label>
                      <input style={s.input} type="number" min="1" value={form.excel.headerRowIndex}
                        onChange={e => setExcel('headerRowIndex', e.target.value)} />
                    </div>
                    <div style={{ ...s.formGroup, width: 100 }}>
                      <label style={s.label}>Veri Başlangıç</label>
                      <input style={s.input} type="number" min="1" value={form.excel.dataStartRowIndex}
                        onChange={e => setExcel('dataStartRowIndex', e.target.value)} />
                    </div>
                  </div>
                </>
              )}

              {form.sourceType === 4 && (
                <>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ ...s.formGroup, width: 130 }}>
                      <label style={s.label}>Mod</label>
                      <select style={s.select} value={form.json.mode}
                        onChange={e => setJson('mode', Number(e.target.value))}>
                        {JSON_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                    <div style={{ ...s.formGroup, flex: 1 }}>
                      <label style={s.label}>
                        {form.json.mode === 1 ? 'Dosya Yolu *' : 'Ham JSON *'}
                      </label>
                      <input style={s.input} value={form.json.source}
                        onChange={e => setJson('source', e.target.value)}
                        placeholder={form.json.mode === 1 ? 'C:\\Imports\\data.json' : '[{"id":1}]'} />
                    </div>
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>Kök Dizi JSONPath</label>
                    <input style={s.input} value={form.json.rootPath}
                      onChange={e => setJson('rootPath', e.target.value)} placeholder="$.data.items" />
                  </div>
                </>
              )}

              {/* Dönüşüm Adımları */}
              <SectionLabel>Dönüşüm Adımları</SectionLabel>
              <TransformStepBuilder
                steps={transformConfig.steps}
                error={transformConfig.error}
                json={form.transformStepsJson}
                onAdd={addTransformStep}
                onJsonChange={value => setF('transformStepsJson', value)}
                onMove={moveTransformStep}
                onOptionChange={updateTransformOption}
                onRemove={removeTransformStep}
                onStepChange={updateTransformStep}
              />
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnPrimary} onClick={handleSave} disabled={saving}>
                {saving ? 'Kaydediliyor...' : (modal === 'edit' ? 'Güncelle' : 'Kaydet')}
              </button>
              <button style={s.btnSecondary} onClick={closeModal}>İptal</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Detay Modalı ── */}
      {modal === 'view' && selected && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={{ ...s.modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>{selected.name}</h3>
              <button style={s.closeBtn} onClick={closeModal}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button style={s.btnSmallWarning} onClick={() => { closeModal(); openEdit(selected); }}>Düzenle</button>
                <button style={s.btnSmallDanger} onClick={() => { closeModal(); setDeleteConfirmOpen(true); }}>Sil</button>
              </div>
              <DetailRow label="Kaynak Tipi"  value={SOURCE_TYPES.find(t => t.value === selected.sourceType)?.label ?? selected.sourceType} />
              <DetailRow label="Zamanlama"    value={selected.cronExpression || '—'} mono />
              <DetailRow label="Durum"        value={STATUS_MAP[selected.lastStatus]?.label ?? '—'} />
              <DetailRow label="Son Çalışma"  value={fmtDate(selected.lastRunAt)} />
              <DetailRow label="Job ID"       value={selected.lastJobId || '—'} mono />
              <DetailRow label="Aktif"        value={selected.isActive ? 'Evet' : 'Hayır'} />
            </div>
            <div style={s.modalFooter}>
              {!selected.isActive && selected.cronExpression && (
                <button style={s.btnPrimary} onClick={() => { closeModal(); handleActivate(selected); }}>
                  Aktifleştir
                </button>
              )}
              <button style={{ ...s.btnPrimary, background: 'var(--success)' }} onClick={() => { closeModal(); handleRun(selected); }}>
                Şimdi Çalıştır
              </button>
              <button style={s.btnSecondary} onClick={closeModal}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Log Modalı ── */}
      {modal === 'logs' && selected && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={{ ...s.modal, maxWidth: 760, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Çalışma Logları — {selected.name}</h3>
              <button style={s.closeBtn} onClick={closeModal}>×</button>
            </div>
            <div style={{ ...s.modalBody, overflowY: 'auto', flex: 1 }}>
              {logsLoading ? (
                <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>Yükleniyor...</p>
              ) : logs.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>Henüz çalışma kaydı yok.</p>
              ) : (
                <table style={{ ...s.table, fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={s.th}>Başlangıç</th>
                      <th style={s.th}>Bitiş</th>
                      <th style={s.th}>Durum</th>
                      <th style={s.th}>Okunan</th>
                      <th style={s.th}>Yazılan</th>
                      <th style={s.th}>Hata</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => {
                      const st = STATUS_MAP[log.status] ?? STATUS_MAP[0];
                      return (
                        <tr key={log.id} style={s.tr}>
                          <td style={s.td}>{fmtDate(log.startedAt)}</td>
                          <td style={s.tdMuted}>{fmtDate(log.completedAt)}</td>
                          <td style={s.td}>
                            <span style={{ color: st.color, fontWeight: 600 }}>● {st.label}</span>
                          </td>
                          <td style={s.td}>{log.rowsRead ?? '—'}</td>
                          <td style={s.td}>{log.rowsWritten ?? '—'}</td>
                          <td style={{ ...s.tdMuted, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={log.errorMessage}>
                            {log.errorMessage || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={closeModal}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {scheduleModalOpen && (
        <div style={s.overlay} onClick={closeScheduleModal}>
          <div style={{ ...s.modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Cron Zamanlama Olustur</h3>
              <button style={s.closeBtn} onClick={closeScheduleModal}>x</button>
            </div>
            <div style={s.modalBody}>
              <div style={styles.scheduleIntro}>
                Zamanlamayi kullanici dostu alanlardan secin. OK dediginizde olusan cron metni ilgili sahaya yazilir.
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Zamanlama Tipi</label>
                <select
                  style={s.select}
                  value={scheduleDraft.mode}
                  onChange={e => setScheduleDraft(current => ({ ...current, mode: e.target.value }))}
                >
                  <option value="everyMinutes">Her X dakikada bir</option>
                  <option value="hourly">Her saat</option>
                  <option value="daily">Her gun</option>
                  <option value="weekly">Haftalik</option>
                  <option value="monthly">Aylik</option>
                </select>
              </div>

              {scheduleDraft.mode === 'everyMinutes' && (
                <div style={s.formGroup}>
                  <label style={s.label}>Dakika Araligi</label>
                  <input
                    style={s.input}
                    type="number"
                    min="1"
                    max="59"
                    value={scheduleDraft.everyMinutes}
                    onChange={e => setScheduleDraft(current => ({ ...current, everyMinutes: e.target.value }))}
                  />
                </div>
              )}

              {scheduleDraft.mode === 'hourly' && (
                <div style={s.formGroup}>
                  <label style={s.label}>Her saatin kacinci dakikasinda?</label>
                  <input
                    style={s.input}
                    type="number"
                    min="0"
                    max="59"
                    value={scheduleDraft.minute}
                    onChange={e => setScheduleDraft(current => ({ ...current, minute: e.target.value }))}
                  />
                </div>
              )}

              {(scheduleDraft.mode === 'daily' || scheduleDraft.mode === 'weekly' || scheduleDraft.mode === 'monthly') && (
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ ...s.formGroup, flex: 1 }}>
                    <label style={s.label}>Saat</label>
                    <input
                      style={s.input}
                      type="number"
                      min="0"
                      max="23"
                      value={scheduleDraft.hour}
                      onChange={e => setScheduleDraft(current => ({ ...current, hour: e.target.value }))}
                    />
                  </div>
                  <div style={{ ...s.formGroup, flex: 1 }}>
                    <label style={s.label}>Dakika</label>
                    <input
                      style={s.input}
                      type="number"
                      min="0"
                      max="59"
                      value={scheduleDraft.minute}
                      onChange={e => setScheduleDraft(current => ({ ...current, minute: e.target.value }))}
                    />
                  </div>
                </div>
              )}

              {scheduleDraft.mode === 'weekly' && (
                <div style={s.formGroup}>
                  <label style={s.label}>Haftanin Gunu</label>
                  <select
                    style={s.select}
                    value={scheduleDraft.dayOfWeek}
                    onChange={e => setScheduleDraft(current => ({ ...current, dayOfWeek: e.target.value }))}
                  >
                    <option value="0">Pazar</option>
                    <option value="1">Pazartesi</option>
                    <option value="2">Sali</option>
                    <option value="3">Carsamba</option>
                    <option value="4">Persembe</option>
                    <option value="5">Cuma</option>
                    <option value="6">Cumartesi</option>
                  </select>
                </div>
              )}

              {scheduleDraft.mode === 'monthly' && (
                <div style={s.formGroup}>
                  <label style={s.label}>Ayin Kacinci Gunu</label>
                  <input
                    style={s.input}
                    type="number"
                    min="1"
                    max="31"
                    value={scheduleDraft.dayOfMonth}
                    onChange={e => setScheduleDraft(current => ({ ...current, dayOfMonth: e.target.value }))}
                  />
                </div>
              )}

              <div style={styles.schedulePreviewBox}>
                <div style={styles.schedulePreviewLabel}>Onizleme</div>
                <div style={styles.schedulePreviewText}>{describeSchedule(scheduleDraft)}</div>
                <code style={styles.schedulePreviewCode}>{buildCronExpression(scheduleDraft)}</code>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnPrimary} onClick={applySchedule}>OK</button>
              <button style={s.btnSecondary} onClick={closeScheduleModal}>Iptal</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmOpen && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirmOpen(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Pipeline Sil</h3>
              <button style={s.closeBtn} onClick={() => setDeleteConfirmOpen(false)}>×</button>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, lineHeight: 1.6 }}>
                <strong>{selected.name}</strong> pipeline kaydını silmek istediğinize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={() => handleDelete(selected)} disabled={deleting}>
                {deleting ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirmOpen(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

/* ─── Küçük yardımcı bileşenler ──────────────────────────────── */
function TransformStepBuilder({
  steps,
  error,
  json,
  onAdd,
  onJsonChange,
  onMove,
  onOptionChange,
  onRemove,
  onStepChange,
}) {
  return (
    <div style={styles.transformBuilder}>
      <div style={styles.transformHeader}>
        <div>
          <div style={styles.transformTitle}>Adimlari form ile olusturun</div>
          <div style={styles.transformHint}>Kaydetme sirasinda ayni JSON formati backend'e gonderilir.</div>
        </div>
        <button type="button" style={s.btnPrimary} onClick={onAdd}>+ Adim Ekle</button>
      </div>

      {error && (
        <div style={styles.transformError}>
          JSON okunamadi: {error}. Yeni adim eklemek mevcut JSON'u temizleyip form formatina alir.
        </div>
      )}

      {!error && steps.length === 0 && (
        <div style={styles.emptyTransform}>Henuz donusum adimi yok. Veri oldugu gibi aktarilir.</div>
      )}

      {!error && steps.map((step, index) => (
        <div key={`${step.order}-${index}`} style={styles.transformStep}>
          <div style={styles.transformStepHeader}>
            <div style={styles.stepBadge}>{index + 1}</div>
            <select
              style={{ ...s.select, flex: 1, marginBottom: 0 }}
              value={step.stepType}
              onChange={e => {
                const stepType = Number(e.target.value);
                onStepChange(index, { stepType, options: defaultOptionsForStep(stepType) });
              }}
            >
              {TRANSFORM_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
            <button type="button" style={styles.iconButton} onClick={() => onMove(index, -1)} disabled={index === 0}>Yukari</button>
            <button type="button" style={styles.iconButton} onClick={() => onMove(index, 1)} disabled={index === steps.length - 1}>Asagi</button>
            <button type="button" style={styles.removeButton} onClick={() => onRemove(index)}>Sil</button>
          </div>

          <TransformStepOptions
            step={step}
            index={index}
            onOptionChange={onOptionChange}
          />
        </div>
      ))}

      <details style={styles.advancedJson}>
        <summary style={styles.advancedSummary}>Gelismis JSON</summary>
        <textarea
          style={{ ...s.input, height: 120, resize: 'vertical', fontFamily: 'monospace', fontSize: 12, marginTop: 10 }}
          value={json}
          onChange={e => onJsonChange(e.target.value)}
          placeholder='[{"stepType":1,"order":1,"options":{"trimStrings":true}}]'
        />
      </details>
    </div>
  );
}

function TransformStepOptions({ step, index, onOptionChange }) {
  const options = step.options || {};

  if (step.stepType === 1) {
    return (
      <div style={styles.optionGrid}>
        <CheckboxField label="Bosluklari kirp" checked={options.trimStrings !== false} onChange={value => onOptionChange(index, 'trimStrings', value)} />
        <CheckboxField label="Bos metinleri null yap" checked={options.nullifyEmpty !== false} onChange={value => onOptionChange(index, 'nullifyEmpty', value)} />
        <CheckboxField label="Tamamen bos satirlari sil" checked={!!options.removeNullRows} onChange={value => onOptionChange(index, 'removeNullRows', value)} />
        <CheckboxField label="Sadece bu kolonlari tut" checked={!!options.keepOnlyColumns} onChange={value => onOptionChange(index, 'keepOnlyColumns', value)} />
        <div style={{ ...s.formGroup, gridColumn: '1 / -1' }}>
          <label style={s.label}>Kolonlar</label>
          <input
            style={s.input}
            value={listToText(options.columns)}
            onChange={e => onOptionChange(index, 'columns', splitList(e.target.value))}
            placeholder="Bos birakilirsa tum kolonlar. Or: Name, City"
          />
        </div>
      </div>
    );
  }

  if (step.stepType === 2) {
    return (
      <div style={styles.optionGrid}>
        <div style={s.formGroup}>
          <label style={s.label}>Tarih Kolonlari</label>
          <input style={s.input} value={listToText(options.dateColumns)} onChange={e => onOptionChange(index, 'dateColumns', splitList(e.target.value))} placeholder="OrderDate, CreatedAt" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Tarih Formati</label>
          <input style={s.input} value={scalarToText(options.dateFormat) || 'yyyy-MM-dd'} onChange={e => onOptionChange(index, 'dateFormat', e.target.value)} placeholder="yyyy-MM-dd" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Sayi Kolonlari</label>
          <input style={s.input} value={listToText(options.numberColumns)} onChange={e => onOptionChange(index, 'numberColumns', splitList(e.target.value))} placeholder="Amount, Total" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Ondalik Basamak</label>
          <input style={s.input} type="number" min="0" value={scalarToText(options.decimalPlaces) || 2} onChange={e => onOptionChange(index, 'decimalPlaces', Number(e.target.value) || 0)} />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Harf Formati Kolonlari</label>
          <input style={s.input} value={listToText(options.casingColumns)} onChange={e => onOptionChange(index, 'casingColumns', splitList(e.target.value))} placeholder="Name, City" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Harf Formati</label>
          <select style={s.select} value={scalarToText(options.casing)} onChange={e => onOptionChange(index, 'casing', e.target.value)}>
            {CASING_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
      </div>
    );
  }

  if (step.stepType === 3) {
    return (
      <div style={styles.optionGrid}>
        <div style={{ ...s.formGroup, gridColumn: '1 / -1' }}>
          <label style={s.label}>Kolon Adi Degistir</label>
          <MappingTextarea
            value={renameMapToText(options.columns)}
            onCommit={value => onOptionChange(index, 'columns', parseRenameMap(value))}
            placeholder={'OldName=NewName\nCustomerCode=Code'}
          />
        </div>
        <div style={{ ...s.formGroup, gridColumn: '1 / -1' }}>
          <label style={s.label}>Deger Esle</label>
          <MappingTextarea
            value={valueMapToText(options.values)}
            onCommit={value => onOptionChange(index, 'values', parseValueMap(value))}
            placeholder={'Status: 1=Active, 0=Inactive\nType: C=Customer, V=Vendor'}
          />
        </div>
      </div>
    );
  }

  if (step.stepType === 4) {
    const operator = scalarToText(options.operator) || 'eq';
    const needsValue = !['isNull', 'notNull', 'notEmpty'].includes(operator);
    return (
      <div style={styles.optionGrid}>
        <div style={s.formGroup}>
          <label style={s.label}>Kolon</label>
          <input style={s.input} value={scalarToText(options.column)} onChange={e => onOptionChange(index, 'column', e.target.value)} placeholder="Status" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Operator</label>
          <select style={s.select} value={operator} onChange={e => onOptionChange(index, 'operator', e.target.value)}>
            {FILTER_OPERATORS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
        {needsValue && (
          <div style={{ ...s.formGroup, gridColumn: '1 / -1' }}>
            <label style={s.label}>Karsilastirma Degeri</label>
            <input style={s.input} value={scalarToText(options.value)} onChange={e => onOptionChange(index, 'value', e.target.value)} placeholder="Active" />
          </div>
        )}
      </div>
    );
  }

  return null;
}

function CheckboxField({ label, checked, onChange }) {
  return (
    <label style={styles.checkboxField}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function MappingTextarea({ value, onCommit, placeholder }) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  return (
    <textarea
      style={{ ...s.input, height: 76, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => onCommit(draft)}
      placeholder={placeholder}
    />
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase',
      letterSpacing: '0.08em', margin: '16px 0 8px', borderBottom: '1px solid var(--border-color)',
      paddingBottom: 4 }}>
      {children}
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'baseline' }}>
      <span style={{ width: 120, flexShrink: 0, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ fontSize: 14, color: 'var(--text-primary)', fontFamily: mono ? 'monospace' : undefined }}>
        {value}
      </span>
    </div>
  );
}

function pill(color) {
  return {
    display: 'inline-block', padding: '2px 10px', borderRadius: 12,
    background: color + '18', color, fontSize: 12, fontWeight: 700,
  };
}

const styles = {
  activateButton: {
    ...s.btnSmallInfo,
    background: '#0E7490',
    color: '#fff',
    marginLeft: 10,
  },
  transformBuilder: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  transformHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    alignItems: 'center',
  },
  transformTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--text-primary)',
  },
  transformHint: {
    marginTop: 4,
    fontSize: 12,
    color: 'var(--text-secondary)',
  },
  transformError: {
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #E53E3E33',
    background: '#E53E3E12',
    color: '#dc2626',
    fontSize: 13,
  },
  emptyTransform: {
    padding: '14px 12px',
    borderRadius: 8,
    border: '1px dashed var(--border-color)',
    color: 'var(--text-secondary)',
    fontSize: 13,
  },
  transformStep: {
    border: '1px solid var(--border-color)',
    borderRadius: 8,
    padding: 12,
    background: 'var(--input-bg)',
  },
  transformStepHeader: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    marginBottom: 12,
  },
  stepBadge: {
    width: 28,
    height: 28,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: '#0073ea12',
    color: 'var(--accent)',
    fontWeight: 800,
    fontSize: 13,
  },
  iconButton: {
    height: 36,
    padding: '0 10px',
    borderRadius: 8,
    border: '1px solid var(--border-color)',
    background: 'var(--modal-bg)',
    color: 'var(--text-secondary)',
    fontWeight: 700,
    cursor: 'pointer',
  },
  removeButton: {
    height: 36,
    padding: '0 10px',
    borderRadius: 8,
    border: '1px solid #E53E3E33',
    background: '#E53E3E12',
    color: '#dc2626',
    fontWeight: 700,
    cursor: 'pointer',
  },
  optionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
  },
  checkboxField: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 42,
    padding: '0 10px',
    borderRadius: 8,
    border: '1px solid var(--border-color)',
    color: 'var(--text-primary)',
    fontSize: 13,
    fontWeight: 600,
  },
  advancedJson: {
    borderTop: '1px solid var(--border-color)',
    paddingTop: 10,
  },
  advancedSummary: {
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    fontSize: 13,
    fontWeight: 700,
  },
  cronFieldWrap: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  cronBuilderButton: {
    flexShrink: 0,
    height: 42,
    padding: '0 14px',
    borderRadius: 12,
    border: '1px solid #0073ea33',
    background: '#0073ea12',
    color: 'var(--accent)',
    fontWeight: 700,
    cursor: 'pointer',
  },
  cronHint: {
    marginTop: 6,
    fontSize: 12,
    color: 'var(--text-secondary)',
  },
  scheduleIntro: {
    marginBottom: 14,
    padding: '12px 14px',
    borderRadius: 12,
    background: 'rgba(0,115,234,0.06)',
    color: 'var(--text-secondary)',
    fontSize: 13,
    lineHeight: 1.5,
  },
  schedulePreviewBox: {
    marginTop: 8,
    padding: 16,
    borderRadius: 14,
    border: '1px solid var(--border-color)',
    background: 'var(--input-bg)',
  },
  schedulePreviewLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--accent)',
    marginBottom: 6,
  },
  schedulePreviewText: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: 8,
  },
  schedulePreviewCode: {
    display: 'inline-block',
    padding: '6px 10px',
    borderRadius: 10,
    background: '#0F172A',
    color: '#F8FAFC',
    fontSize: 13,
  },
};
